import { mkdir, writeFile } from 'node:fs/promises'
import { realpathSync } from 'node:fs'
import { join, resolve } from 'node:path'
import { parseArgs } from 'node:util'
import { fileURLToPath } from 'node:url'
import { loadProfiles, resolveApiOrigin, resolveTokenWithKeychain, saveProfile, systemKeychain } from './config.js'
import { NovelHttpClient } from './httpClient.js'
import { startDiagnosticsMcpServer } from './mcpServer.js'
import { jsonError, jsonSuccess } from './output.js'
import { openBrowser } from './browser.js'
import { writeExportReadme } from './exportReadme.js'

type ExportArtifact = { id: string; phase: string; content_status: string; content_markdown?: string }
type DiagnosticExport = {
  run: { id: string; project_id?: string; thread_id?: string }
  artifacts: ExportArtifact[]
  raw_included?: boolean
  snapshot_fidelity?: string
  [key: string]: unknown
}

export type NovelCommand =
  | { kind: 'run-diagnose' | 'project-diagnose' | 'project-inspect' | 'project-export' | 'project-conversation' | 'message-diagnose' | 'thread-pull' | 'analysis-inspect' | 'analysis-pull'; id: string }
  | { kind: 'project-audit'; id: string }
  | { kind: 'artifact-pull'; id: string; phase: string }
  | { kind: 'doctor' | 'init' | 'mcp' | 'login' | 'logout' | 'whoami' | 'auth-set-token' | 'auth-clear-token' }

function usage(): never {
  throw new Error([
    'Usage: novel <command> [options]',
    'Commands:',
    '  run diagnose <run-id>',
    '  project diagnose|inspect|export|conversation|audit <project-id>',
    '  message diagnose <message-id>',
    '  thread pull <thread-id>',
    '  artifact pull <project-id> <phase>',
    '  analysis inspect|pull <analysis-id>',
    '  init --api <origin> [--profile <name>] --yes',
    '  login [--api <origin>] [--profile <name>]',
    '  logout|whoami [--profile <name>]',
    '  doctor',
    '  mcp [--profile <name>]',
    '  auth set-token|clear-token [--profile <name>] --yes',
  ].join('\n'))
}

/** pnpm forwards a leading `--`; an installed `novel` binary does not. */
export function normalizeCliArgs(argv: string[]): string[] {
  return argv[0] === '--' ? argv.slice(1) : argv
}

/** `npm link` invokes the bin through a symlink, so compare resolved files. */
export function isCliEntrypoint(argvPath: string | undefined, entryPath: string, resolvePath: (path: string) => string = realpathSync): boolean {
  if (!argvPath) return false
  try { return resolvePath(argvPath) === entryPath } catch { return false }
}

export function parseNovelCommand(argv: string[]): NovelCommand | null {
  const [scope, action, id, phase] = argv
  if (scope === 'doctor') return { kind: 'doctor' }
  if (scope === 'init') return { kind: 'init' }
  if (scope === 'login') return { kind: 'login' }
  if (scope === 'logout') return { kind: 'logout' }
  if (scope === 'whoami') return { kind: 'whoami' }
  if (scope === 'mcp') return { kind: 'mcp' }
  if (scope === 'auth' && action === 'set-token') return { kind: 'auth-set-token' }
  if (scope === 'auth' && action === 'clear-token') return { kind: 'auth-clear-token' }
  if (!id) return null
  if (scope === 'run' && action === 'diagnose') return { kind: 'run-diagnose', id }
  if (scope === 'project' && action === 'diagnose') return { kind: 'project-diagnose', id }
  if (scope === 'project' && action === 'inspect') return { kind: 'project-inspect', id }
  if (scope === 'project' && action === 'export') return { kind: 'project-export', id }
  if (scope === 'project' && action === 'conversation') return { kind: 'project-conversation', id }
  if (scope === 'project' && action === 'audit') return { kind: 'project-audit', id }
  if (scope === 'message' && action === 'diagnose') return { kind: 'message-diagnose', id }
  if (scope === 'thread' && action === 'pull') return { kind: 'thread-pull', id }
  if (scope === 'artifact' && action === 'pull' && phase) return { kind: 'artifact-pull', id, phase }
  if (scope === 'analysis' && action === 'inspect') return { kind: 'analysis-inspect', id }
  if (scope === 'analysis' && action === 'pull') return { kind: 'analysis-pull', id }
  return null
}

async function requestJson(pathname: string, api: string, token: string, debugUserId?: string): Promise<any> {
  return new NovelHttpClient({ api, token, debugUserId }).getJson(pathname)
}

async function browserLogin(api: string, profile: string, noBrowser = false): Promise<{ userId: string; verificationUrl: string }> {
  const client = new NovelHttpClient({ api, token: '' })
  const start = await client.postJson<{ device_secret: string; verification_url: string; expires_at: string; poll_interval_seconds: number }>('/api/cli-auth/device/start', {}, false)
  if (noBrowser) console.error(`Open this URL to continue Novel login: ${start.verification_url}`)
  else await openBrowser(start.verification_url).catch(() => console.error(`Open this URL to continue Novel login: ${start.verification_url}`))
  const deadline = Date.parse(start.expires_at)
  while (Date.now() < deadline) {
    const response = await client.postJson<{ status?: string; token?: string; user_id?: string }>('/api/cli-auth/device/token', { device_secret: start.device_secret }, false).catch((error: any) => {
      if (error?.status === 202) return { status: 'pending' }
      throw error
    })
    if (response.status === 'approved' && response.token && response.user_id) {
      await systemKeychain.write!(profile, response.token)
      return { userId: response.user_id, verificationUrl: start.verification_url }
    }
    await new Promise(resolve => setTimeout(resolve, start.poll_interval_seconds * 1000))
  }
  throw userError('LOGIN_EXPIRED', 'Browser login timed out before approval.', 'Run novel login again.')
}

async function exportJson(pathname: string, api: string, token: string, destination: string, filename: string, debugUserId?: string): Promise<any> {
  const data = await requestJson(pathname, api, token, debugUserId)
  await mkdir(destination, { recursive: true })
  await writeFile(join(destination, filename), JSON.stringify(data, null, 2) + '\n', 'utf8')
  return data
}

type CommandResult = { data: unknown; files?: string[]; warnings?: string[] }

async function exportDiagnostic(runId: string, api: string, token: string, out: string, debugUserId?: string): Promise<CommandResult> {
  const data = await requestJson(`/api/skill-debug/diagnostics/runs/${encodeURIComponent(runId)}`, api, token, debugUserId) as DiagnosticExport
  const destination = resolve(out, data.run.id)
  await mkdir(destination, { recursive: true })
  const manifest = { ...data, artifacts: data.artifacts.map(({ content_markdown, ...artifact }) => artifact) }
  const files = [join(destination, 'diagnostic.json')]
  await writeFile(join(destination, 'diagnostic.json'), JSON.stringify(manifest, null, 2) + '\n', 'utf8')
  for (const artifact of data.artifacts) {
    if (artifact.content_status !== 'current' || typeof artifact.content_markdown !== 'string') continue
    await mkdir(join(destination, 'artifacts'), { recursive: true })
    const file = join(destination, 'artifacts', `${artifact.phase}-${artifact.id}.md`)
    await writeFile(file, artifact.content_markdown, 'utf8')
    files.push(file)
  }
  files.push(await writeExportReadme({
    kind: 'run-diagnostic',
    targetId: data.run.id,
    destination,
    files,
    runId: data.run.id,
    projectId: typeof data.run.project_id === 'string' ? data.run.project_id : undefined,
    threadId: typeof data.run.thread_id === 'string' ? data.run.thread_id : undefined,
    rawIncluded: typeof data.raw_included === 'boolean' ? data.raw_included : undefined,
    snapshotFidelity: typeof data.snapshot_fidelity === 'string' ? data.snapshot_fidelity : undefined,
  }))
  return { data: { run_id: data.run.id }, files }
}

async function exportMessageDiagnostic(messageId: string, api: string, token: string, out: string, includeRaw = false, debugUserId?: string): Promise<CommandResult> {
  const rawQuery = includeRaw ? '?include_raw=true' : ''
  const data = await requestJson(`/api/skill-debug/diagnostics/messages/${encodeURIComponent(messageId)}${rawQuery}`, api, token, debugUserId)
  const destination = resolve(out, messageId)
  await mkdir(destination, { recursive: true })
  const files = [join(destination, 'message.json'), join(destination, 'tool-calls.json')]
  await writeFile(files[0], JSON.stringify(data, null, 2) + '\n', 'utf8')
  if (data.userMessage?.text) {
    const file = join(destination, 'user-message.md')
    await writeFile(file, data.userMessage.text, 'utf8')
    files.push(file)
  }
  if (data.message?.text) {
    const file = join(destination, 'assistant-message.md')
    await writeFile(file, data.message.text, 'utf8')
    files.push(file)
  }
  await writeFile(files[1], JSON.stringify(data.toolCalls ?? [], null, 2) + '\n', 'utf8')
  files.push(await writeExportReadme({
    kind: 'message-diagnostic',
    targetId: messageId,
    destination,
    files,
    messageId,
    projectId: typeof data.projectId === 'string' ? data.projectId : undefined,
    threadId: typeof data.threadId === 'string' ? data.threadId : undefined,
    runId: typeof data.qaRun?.id === 'string' ? data.qaRun.id : undefined,
    rawIncluded: typeof data.raw_included === 'boolean' ? data.raw_included : includeRaw,
    snapshotFidelity: typeof data.snapshotFidelity === 'string'
      ? data.snapshotFidelity
      : typeof data.snapshot_fidelity === 'string' ? data.snapshot_fidelity : undefined,
  }))
  return { data: { message_id: messageId }, files }
}

const capabilitySchema = {
  schema_version: 1,
  name: 'novel',
  project_content_write: false,
  diagnostic_writes: ['project audit'],
  commands: [
    'run diagnose <run-id>',
    'project diagnose <project-id>', 'project inspect <project-id>', 'project export <project-id>', 'project conversation <project-id>', 'project audit <project-id> --phase <phase>',
    'message diagnose <message-id>', 'thread pull <thread-id>', 'artifact pull <project-id> <phase>',
    'analysis inspect <analysis-id>', 'analysis pull <analysis-id>', 'doctor',
  ],
  options: ['--api <origin>', '--profile <name>', '--token <token>', '--out <directory>', '--phase <phase>', '--run <run-id>', '--include-raw', '--debug-user-id <id>', '--json'],
}

function userError(code: string, message: string, hint?: string): Error & { code: string; hint?: string } {
  return Object.assign(new Error(message), { code, hint })
}

export async function execute(argv: string[], environment: NodeJS.ProcessEnv = process.env): Promise<{ result: CommandResult; environment: string }> {
  const command = parseNovelCommand(argv)
  if (!command) usage()
  const optionOffset = command.kind === 'doctor' || command.kind === 'init' || command.kind === 'mcp' || command.kind === 'login' || command.kind === 'logout' || command.kind === 'whoami' ? 1 : command.kind.startsWith('auth-') ? 2 : command.kind === 'artifact-pull' ? 4 : 3
  const { values } = parseArgs({ args: argv.slice(optionOffset), options: {
    api: { type: 'string' }, token: { type: 'string' }, out: { type: 'string' }, profile: { type: 'string' },
    label: { type: 'string' }, yes: { type: 'boolean' }, schema: { type: 'boolean' }, json: { type: 'boolean' },
    phase: { type: 'string' }, run: { type: 'string' },
    'include-raw': { type: 'boolean' }, 'debug-user-id': { type: 'string' }, 'no-browser': { type: 'boolean' },
  } })
  const profileName = values.profile ?? 'default'
  if (values.schema) return { result: { data: capabilitySchema }, environment: profileName }
  if (command.kind === 'init') {
    if (!values.api || !values.yes) throw userError('INIT_CONFIRMATION_REQUIRED', 'Non-interactive initialization requires --yes.', 'Run: novel init --api <origin> --yes')
    await saveProfile({ name: profileName, api: values.api, label: values.label })
    return { result: { data: { profile: profileName, api: values.api } }, environment: profileName }
  }
  if (command.kind === 'auth-set-token') {
    if (!values.yes || !values.token) throw userError('AUTH_CONFIRMATION_REQUIRED', 'Keychain storage requires --token and --yes.', 'Run: novel auth set-token --token <token> --profile <name> --yes')
    await systemKeychain.write!(profileName, values.token)
    return { result: { data: { profile: profileName, token_source: 'keychain' } }, environment: profileName }
  }
  if (command.kind === 'auth-clear-token') {
    if (!values.yes) throw userError('AUTH_CONFIRMATION_REQUIRED', 'Keychain removal requires --yes.', 'Run: novel auth clear-token --profile <name> --yes')
    await systemKeychain.remove!(profileName)
    return { result: { data: { profile: profileName, token_source: 'missing' } }, environment: profileName }
  }
  const profiles = await loadProfiles()
  const api = resolveApiOrigin({
    explicit: values.api,
    profile: profiles.profiles[profileName]?.api,
    environment,
    allowDefault: command.kind === 'login',
  })
  if (!api) throw userError('API_REQUIRED', 'Novel API origin is required.', 'Use --api <origin>, set JUJING_API, or run novel login first.')
  let token = await resolveTokenWithKeychain({ token: values.token, environment, profile: profileName })
  const out = values.out ?? join(process.cwd(), '.jujing', 'diagnostics')

  if (command.kind === 'login') {
    if (profiles.profiles[profileName]?.api !== api) await saveProfile({ name: profileName, api, label: values.label })
    const login = await browserLogin(api, profileName, values['no-browser'])
    return { result: { data: { profile: profileName, user_id: login.userId, ...(values['no-browser'] ? { verification_url: login.verificationUrl } : {}) } }, environment: profileName }
  }
  if (command.kind === 'logout') {
    await systemKeychain.remove!(profileName)
    return { result: { data: { profile: profileName, logged_out: true } }, environment: profileName }
  }
  if (command.kind === 'whoami') {
    if (!token.value) throw userError('AUTH_REQUIRED', 'Novel login is required.', 'Run: novel login --profile <name>')
    const me = await requestJson('/api/cli-auth/me', api, token.value)
    return { result: { data: { profile: profileName, ...me, token_source: token.source } }, environment: profileName }
  }

  // Interactive users should get the same browser-login recovery as `novel login`;
  // CI and explicit --token calls remain deterministic and non-interactive.
  if (!token.value && !values.token && process.stdin.isTTY && process.stdout.isTTY) {
    await browserLogin(api, profileName)
    token = await resolveTokenWithKeychain({ environment, profile: profileName })
  }

  if (command.kind === 'mcp') throw userError('MCP_STDIO_ONLY', 'Use main() to start MCP stdio mode.')

  if (command.kind === 'doctor') {
    if (!token.value) return { result: { data: { api, token_source: token.source, healthy: false }, warnings: ['JUJING_TOKEN or --token is required for the health check.'] }, environment: profileName }
    return { result: { data: { ...(await requestJson('/api/skill-debug/diagnostics/health', api, token.value, values['debug-user-id'])), token_source: token.source } }, environment: profileName }
  }
  if (!token.value) throw userError('AUTH_REQUIRED', 'Novel API token is required.', 'Pass --token or set JUJING_TOKEN.')
  if (command.kind === 'run-diagnose') {
    return { result: await exportDiagnostic(command.id, api, token.value, out, values['debug-user-id']), environment: profileName }
  }
  if (command.kind === 'project-audit') {
    if (!values.phase) throw userError('PHASE_REQUIRED', 'Project audit requires --phase.', 'Use --phase phase0|phase1|phase2|phase3.')
    const data = await new NovelHttpClient({ api, token: token.value, debugUserId: values['debug-user-id'] }).postJson(
      `/api/skill-debug/diagnostics/projects/${encodeURIComponent(command.id)}/audits`,
      { phase: values.phase, run_id: values.run ?? null },
    )
    return { result: { data }, environment: profileName }
  }
  if (command.kind === 'message-diagnose') {
    return {
      result: await exportMessageDiagnostic(command.id, api, token.value, out, values['include-raw'], values['debug-user-id']),
      environment: profileName,
    }
  }
  if (command.kind === 'thread-pull') {
    const destination = resolve(out, command.id)
    const data = await exportJson(`/api/skill-debug/diagnostics/threads/${encodeURIComponent(command.id)}/export?include_raw=true`, api, token.value, destination, 'thread.json', values['debug-user-id'])
    const files = [join(destination, 'thread.json')]
    files.push(await writeExportReadme({
      kind: 'thread-export', targetId: command.id, destination, files, threadId: command.id,
      projectId: typeof data.project_id === 'string' ? data.project_id : undefined, rawIncluded: true,
    }))
    return { result: { data: { thread_id: command.id, message_count: data.messages?.length ?? 0, tool_call_count: data.tool_calls?.length ?? 0 }, files }, environment: profileName }
  }
  if (command.kind === 'project-conversation') {
    const destination = resolve(out, command.id)
    const data = await exportJson(`/api/skill-debug/diagnostics/projects/${encodeURIComponent(command.id)}/conversation/export?include_raw=true`, api, token.value, destination, 'project-conversation.json', values['debug-user-id'])
    const files = [join(destination, 'project-conversation.json')]
    files.push(await writeExportReadme({
      kind: 'project-conversation', targetId: command.id, destination, files, projectId: command.id, rawIncluded: true,
    }))
    return { result: { data: { project_id: command.id, thread_count: data.threads?.length ?? 0, tool_call_count: data.tool_call_count ?? 0 }, files }, environment: profileName }
  }
  if (command.kind === 'project-inspect') {
    const destination = resolve(out, command.id)
    const data = await exportJson(`/api/skill-debug/diagnostics/projects/${encodeURIComponent(command.id)}/inspect`, api, token.value, destination, 'project-inspect.json', values['debug-user-id'])
    const files = [join(destination, 'project-inspect.json')]
    files.push(await writeExportReadme({ kind: 'project-inspect', targetId: command.id, destination, files, projectId: command.id }))
    return { result: { data, files }, environment: profileName }
  }
  if (command.kind === 'project-export') {
    const destination = resolve(out, command.id)
    const files = [join(destination, 'project-export.json')]
    const exported = await exportJson(`/api/skill-debug/diagnostics/projects/${encodeURIComponent(command.id)}/export`, api, token.value, destination, 'project-export.json', values['debug-user-id'])
    for (const artifact of exported.artifacts ?? []) {
      if (artifact.content_status !== 'current' || typeof artifact.phase !== 'string') continue
      const artifactDirectory = join(destination, 'artifacts')
      await mkdir(artifactDirectory, { recursive: true })
      const { content_markdown, ...metadata } = artifact
      const metadataFile = join(artifactDirectory, `${artifact.phase}.json`)
      await writeFile(metadataFile, JSON.stringify(metadata, null, 2) + '\n', 'utf8')
      files.push(metadataFile)
      if (typeof content_markdown === 'string') {
        const file = join(artifactDirectory, `${artifact.phase}.md`)
        await writeFile(file, content_markdown, 'utf8')
        files.push(file)
      }
    }
    files.push(await writeExportReadme({ kind: 'project-export', targetId: command.id, destination, files, projectId: command.id }))
    return { result: { data: { project_id: command.id }, files }, environment: profileName }
  }
  if (command.kind === 'artifact-pull') {
    const destination = resolve(out, command.id, 'artifacts')
    const files = [join(destination, `${command.phase}.json`)]
    const data = await exportJson(`/api/skill-debug/diagnostics/artifacts/${encodeURIComponent(command.id)}/${encodeURIComponent(command.phase)}`, api, token.value, destination, `${command.phase}.json`, values['debug-user-id'])
    if (typeof data.content_markdown === 'string') {
      const file = join(destination, `${command.phase}.md`)
      await writeFile(file, data.content_markdown, 'utf8')
      files.push(file)
    }
    files.push(await writeExportReadme({ kind: 'artifact-export', targetId: `${command.id}:${command.phase}`, destination, files, projectId: command.id }))
    return { result: { data: { project_id: command.id, phase: command.phase }, files }, environment: profileName }
  }
  if (command.kind === 'analysis-inspect') {
    const destination = resolve(out, command.id)
    const data = await exportJson(`/api/skill-debug/diagnostics/analyses/${encodeURIComponent(command.id)}`, api, token.value, destination, 'analysis-inspect.json', values['debug-user-id'])
    const files = [join(destination, 'analysis-inspect.json')]
    files.push(await writeExportReadme({ kind: 'analysis-inspect', targetId: command.id, destination, files, analysisId: command.id }))
    return { result: { data, files }, environment: profileName }
  }
  if (command.kind === 'analysis-pull') {
    const destination = resolve(out, command.id)
    const files = [join(destination, 'analysis.json')]
    const data = await exportJson(`/api/skill-debug/diagnostics/analyses/${encodeURIComponent(command.id)}/export`, api, token.value, destination, 'analysis.json', values['debug-user-id'])
    for (const product of data.products ?? []) {
      if (typeof product.content_markdown !== 'string') continue
      await mkdir(join(destination, 'products'), { recursive: true })
      const file = join(destination, 'products', `${product.product_type}-${product.id}.md`)
      await writeFile(file, product.content_markdown, 'utf8')
      files.push(file)
    }
    files.push(await writeExportReadme({ kind: 'analysis-export', targetId: command.id, destination, files, analysisId: command.id }))
    return { result: { data: { analysis_id: command.id }, files }, environment: profileName }
  }
  const runs = await requestJson(`/api/skill-debug/diagnostics/projects/${encodeURIComponent(command.id)}/runs`, api, token.value, values['debug-user-id']) as { runs: Array<{ id: string }> }
  if (!runs.runs[0]?.id) throw new Error(`No QA diagnostic runs found for project ${command.id}`)
  return { result: await exportDiagnostic(runs.runs[0].id, api, token.value, out, values['debug-user-id']), environment: profileName }
}

export async function main(argv: string[]): Promise<void> {
  const json = argv.includes('--json')
  try {
    if (argv[0] === 'mcp') {
      const { values } = parseArgs({ args: argv.slice(1), options: {
        api: { type: 'string' }, token: { type: 'string' }, profile: { type: 'string' },
      } })
      const profileName = values.profile ?? 'default'
      const profiles = await loadProfiles()
      const api = resolveApiOrigin({ explicit: values.api, profile: profiles.profiles[profileName]?.api, environment: process.env, allowDefault: false })
      if (!api) throw userError('API_REQUIRED', 'Novel API origin is required.', 'Use --api <origin>, set JUJING_API, or run novel login first.')
      const token = await resolveTokenWithKeychain({ token: values.token, environment: process.env, profile: profileName })
      if (!token.value) throw userError('AUTH_REQUIRED', 'Novel API token is required.', 'Pass --token or set JUJING_TOKEN.')
      await startDiagnosticsMcpServer(new NovelHttpClient({ api, token: token.value }))
      return
    }
    const { result, environment } = await execute(argv)
    if (json) console.log(jsonSuccess(result.data, { environment, files: result.files, warnings: result.warnings }))
    else if (result.files?.[0]) console.log(result.files[0])
    else console.log(JSON.stringify(result.data, null, 2))
  } catch (error) {
    const known = error as Error & { code?: string; hint?: string }
    if (json) console.log(jsonError({ code: known.code ?? 'COMMAND_FAILED', message: known.message, hint: known.hint }, 'unknown'))
    else console.error(known.message)
    process.exitCode = 1
  }
}

if (isCliEntrypoint(process.argv[1], fileURLToPath(import.meta.url))) {
  void main(normalizeCliArgs(process.argv.slice(2)))
}
